import get from "lodash/get";
import React, { useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useGetAllDealersForSelect } from "~/hooks/Selectors/useGetAllDealersForSelect";
import { REG_TYPE2 } from "~/pages/Accounting/AccountingPeriod/options";
import {
  getReportYearOptions,
  QUARTER_OPTIONS,
} from "~/pages/Accounting/model/periodOptions";
import {
  REPORT_COLUMN_CODES,
  REPORT_COLUMN_CODES_FORM2,
} from "~/pages/Accounting/model/reportColumnCodes";
import {
  AccountingReportColumn,
  useGetAccountingConditionsLazyQuery,
  useGetAccountingFileDealerConditionsLazyQuery,
  useGetAccountingFileDealerMonitoringLazyQuery,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { handlePopupScroll } from "~/shared/lib/handlePopupScroll";
import { Field } from "~/shared/ui/Field";

const accountingReportFormId = "AccountingReportForm";

/** Режимы модалки соответствуют значениям openModal, приходящим из URL. */
type ReportMode = "ReportMonitoring" | "ReportConditions" | "ReportColumns";

const MODE_TITLE_KEY: Record<ReportMode, string> = {
  ReportMonitoring: "accounting.ReportMonitoring",
  ReportConditions: "accounting.ReportConditions",
  ReportColumns: "accounting.ReportColumns",
};

const REPORT_OPTIONS = REG_TYPE2 as ("Both" | "First" | "Second")[];

const decodeColumnLabel = (column: string) => {
  let base = column.slice(1);
  if (base.endsWith("__")) base = base.slice(0, -2) + "==";
  if (base.endsWith("_")) base = base.slice(0, -1) + "=";
  return Buffer.from(base, "base64").toString("utf-8");
};

const buildColumnOptions = (values: string[]) =>
  values.map((value) => ({ value, label: decodeColumnLabel(value) }));

const COLUMNS_ALL = buildColumnOptions(REPORT_COLUMN_CODES);
const COLUMNS_SECOND = buildColumnOptions(REPORT_COLUMN_CODES_FORM2);
const COLUMNS_FIRST = buildColumnOptions(
  REPORT_COLUMN_CODES.filter(
    (item) => !REPORT_COLUMN_CODES_FORM2.includes(item),
  ),
);

interface FormValues {
  DealerIds?: string[];
  fromYear?: number;
  toYear?: number;
  fromQuarter?: number;
  toQuarter?: number;
  columns?: string[];
  report?: "Both" | "First" | "Second";
}

const buildSchema = (mode: ReportMode, t: (key: string) => string) => {
  const requiredNumber = () =>
    yup
      .number()
      .typeError(t("validation.required"))
      .required(t("validation.required"));

  if (mode === "ReportColumns") {
    return yup.object().shape({
      DealerIds: yup
        .array()
        .of(yup.string())
        .min(1, t("validation.required"))
        .required(t("validation.required")),
    });
  }

  return yup.object().shape({
    fromYear: requiredNumber(),
    toYear: requiredNumber(),
    fromQuarter: requiredNumber(),
    toQuarter: requiredNumber(),
    DealerIds: yup
      .array()
      .of(yup.string())
      .min(1, t("validation.required"))
      .required(t("validation.required")),
  });
};

interface AccountingReportModalProps {
  mode: ReportMode;
}

const AccountingReportModal = ({ mode }: AccountingReportModalProps) => {
  const { t } = useTranslation();
  const setDataReplace = useSetLocationData(true);
  const { openModal } = useGetLocationData();
  const { params } = useRouteMatch();

  const isOpen = openModal === mode;
  const isColumns = mode === "ReportColumns";

  const yearOptions = useMemo(getReportYearOptions, []);

  const reportOptions = useMemo(
    () =>
      REPORT_OPTIONS.map((value) => ({
        value,
        label: t(`accounting.${value}`),
      })),
    [t],
  );

  const {
    dealers: dealershipsForSelect,
    onScrollBottomDealers,
    searchDealer,
    loading: loadingDealers,
  } = useGetAllDealersForSelect({ searchDebounceMs: 400 });

  const schema = useMemo(() => buildSchema(mode, t), [mode, t]);

  const { control, handleSubmit, watch, errors, reset } = useForm<FormValues>({
    defaultValues: {
      report: "Both",
      DealerIds: [],
      columns: [],
    },
    resolver: yupResolver(schema),
  });

  const reportValue = watch("report") ?? "Both";
  const columnsOptions = useMemo(() => {
    if (reportValue === "First") return COLUMNS_FIRST;
    if (reportValue === "Second") return COLUMNS_SECOND;
    return COLUMNS_ALL;
  }, [reportValue]);

  const { notifyReport } = useNotify();

  const notifyOnHash = (hash?: string | null) => {
    if (!hash) return;
    notifyReport(hash);
  };

  const [runMonitoringQuery, monitoringResult] =
    useGetAccountingFileDealerMonitoringLazyQuery({
      onCompleted: (data) =>
        notifyOnHash(
          get(data, "AccountingGetter.getFileDealerMonitoring.fileHash"),
        ),
      fetchPolicy: "no-cache",
    });
  const [runConditionsQuery, conditionsResult] =
    useGetAccountingConditionsLazyQuery({
      onCompleted: (data) =>
        notifyOnHash(
          get(data, "AccountingGetter.getFileDealerConditions.fileHash"),
        ),
      fetchPolicy: "no-cache",
    });
  const [runColumnsQuery, columnsResult] =
    useGetAccountingFileDealerConditionsLazyQuery({
      onCompleted: (data) =>
        notifyOnHash(
          get(data, "AccountingGetter.getFileReportFileColumns.fileHash"),
        ),
      fetchPolicy: "no-cache",
    });

  const submitLoading =
    monitoringResult.loading ||
    conditionsResult.loading ||
    columnsResult.loading;

  const handleCloseModal = () => {
    reset();
    setDataReplace("openModal", "");
  };

  const onSubmit = (data: FormValues) => {
    const DealerIds = data.DealerIds ?? [];

    if (mode === "ReportColumns") {
      const chosenColumns = data.columns ?? [];
      const columnsForRequest =
        chosenColumns.length > 0
          ? chosenColumns
          : reportValue === "First"
            ? REPORT_COLUMN_CODES.filter(
                (item) => !REPORT_COLUMN_CODES_FORM2.includes(item),
              )
            : reportValue === "Second"
              ? REPORT_COLUMN_CODES_FORM2
              : REPORT_COLUMN_CODES;
      runColumnsQuery({
        variables: {
          DealerIds,
          columns: columnsForRequest as unknown as AccountingReportColumn[],
          PeriodId: params["period"],
        },
      });
      handleCloseModal();
      return;
    }

    // Историческое поведение: значения from/to меняются местами
    // при передаче на бэкенд — сохраняем совместимость с legacy-формой.
    const periodVariables = {
      DealerIds,
      fromYear: data.toYear,
      toYear: data.fromYear,
      toQuarter: data.fromQuarter,
      fromQuarter: data.toQuarter,
    };

    if (mode === "ReportConditions") {
      runConditionsQuery({ variables: periodVariables });
    } else {
      runMonitoringQuery({ variables: periodVariables });
    }
    handleCloseModal();
  };

  return (
    <Modal
      open={isOpen}
      title={t(MODE_TITLE_KEY[mode])}
      onCancel={handleCloseModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: accountingReportFormId,
        children: t("ui.download_file"),
        loading: submitLoading,
      }}
    >
      <form
        id={accountingReportFormId}
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        {!isColumns && (
          <>
            <Field
              type="select"
              name="toYear"
              control={control}
              label={t("accounting.toYear")}
              options={yearOptions}
              error={errors["toYear"]?.message}
            />
            <Field
              type="select"
              name="toQuarter"
              control={control}
              label={t("accounting.toQuarter")}
              options={QUARTER_OPTIONS}
              error={errors["toQuarter"]?.message}
            />
            <Field
              type="select"
              name="fromYear"
              control={control}
              label={t("accounting.fromYear")}
              options={yearOptions}
              error={errors["fromYear"]?.message}
            />
            <Field
              type="select"
              name="fromQuarter"
              control={control}
              label={t("accounting.fromQuarter")}
              options={QUARTER_OPTIONS}
              error={errors["fromQuarter"]?.message}
            />
          </>
        )}
        <Field
          type="select"
          name="DealerIds"
          control={control}
          label={t("dealership.dealerships")}
          mode="multiple"
          options={dealershipsForSelect}
          showSearch
          filterOption={false}
          onSearch={searchDealer}
          onPopupScroll={(e) => handlePopupScroll(e, onScrollBottomDealers)}
          loading={loadingDealers}
          error={
            (errors["DealerIds"] as { message?: string } | undefined)?.message
          }
        />
        {isColumns && (
          <>
            <Field
              type="select"
              name="report"
              control={control}
              label={t("accounting.ReportCheckFilter")}
              options={reportOptions}
            />
            <Field
              type="select"
              name="columns"
              control={control}
              label={t("accounting.columns_chips")}
              mode="multiple"
              options={columnsOptions}
              showSearch
              optionFilterProp="label"
            />
          </>
        )}
      </form>
    </Modal>
  );
};

export default AccountingReportModal;
